// React hooks for SHOW METRICS / SHOW * / /health. Each hook is a thin
// wrapper around a singleton in metrics-store.js, so:
//   - polling loops survive route changes (the Monitor sparkline keeps
//     its history when you navigate to /cluster and back),
//   - duplicate (sql, interval) subscriptions across the page tree share
//     one polling loop (e.g. the chrome footer + the Home page poll
//     SHOW METRICS once instead of twice),
//   - returning to a page sees a warm cache instead of a flash-of-empty.
//
// The exported function signatures and return shapes are unchanged from
// the pre-store version, so existing call sites keep working.

import {
  metricsSource,
  healthSource,
  sqlSource,
  nodesSource,
  clusterSqlSource,
  clusterMetricsSource,
  s3StatusSource,
  useSource,
} from './metrics-store.js';

export {
  HLC_THRESHOLD,
  isHlc,
  hlcToMs,
  hlcDeltaToMs,
  fmtHlcRelative,
  fmtHlcDuration,
  parseMetrics,
  deriveRates,
} from './use-metrics-utils.js';

// Cluster-wide summary used by the always-visible chrome (top badge,
// sidebar footer). Polls slowly (15s default) so the chrome doesn't
// flicker. Returns:
//   { nodeCount, leaderRss, totalDiskBytes, s3Enabled, s3 { lag,pending,synced,local }, totalQueries, slowQueries }
// Any field is null/undefined while the relevant request is in flight or errored.
//
// All sources go through the connected node — no per-peer addressing —
// so the same code works behind a reverse proxy that exposes a single
// endpoint. Disk totals come from `SHOW DISK STATUS ON ALL NODES`.
export function useClusterSummary({ intervalMs = 15000 } = {}) {
  const health = useHealth('', intervalMs);
  const metrics = useMetrics('', intervalMs);
  const s3 = useSqlRows('SHOW S3 STATUS', '', intervalMs);
  const disk = useClusterSqlRows('SHOW DISK STATUS ON ALL NODES', intervalMs);

  // Sum segment_total_bytes across the nodes that responded. Offline nodes
  // contribute nothing rather than poisoning the total.
  let totalDiskBytes = 0;
  if (disk.byNode) {
    for (const rows of Object.values(disk.byNode)) {
      for (const [k, v] of rows) {
        if (k === 'segment_total_bytes' && Number.isFinite(Number(v))) {
          totalDiskBytes += Number(v);
        }
      }
    }
  }

  // S3 status: leader returns 4 named cols; followers return {columns:['s3'], data:[['disabled']]}.
  const s3Enabled = !!(s3.columns && s3.columns.length > 1 && s3.rows?.[0]);
  const s3Row = s3Enabled ? s3.rows[0] : null;
  const s3State = s3Row ? {
    local:   s3Row[s3.columns.indexOf('local_txn')],
    synced:  s3Row[s3.columns.indexOf('synced_txn')],
    lag:     s3Row[s3.columns.indexOf('txn_lag')],
    pending: s3Row[s3.columns.indexOf('pending_segments')],
  } : null;

  // `unreachable` flips true when the most-recent /health (or SHOW METRICS)
  // failed — even though `health` retains its last-good payload, callers
  // using nodeCount/leaderRss for chrome should treat the cluster as down.
  const unreachable = !!(health.error || metrics.error);
  return {
    nodeCount: health.health?.peers?.length ?? null,
    leaderRss: health.health?.rss_bytes ?? health.health?.memory_total_bytes ?? null,
    totalDiskBytes,
    s3Enabled,
    s3: s3State,
    totalQueries: metrics.latest?.['dml.queries_total'] ?? null,
    slowQueries:  metrics.latest?.['query.slow_total'] ?? null,
    error: health.error || metrics.error || null,
    unreachable,
  };
}

// Poll SHOW METRICS on a single node. Returns
//   { latest, prev, sampledAt, prevSampledAt, error }
// `latest`/`prev` are parsed metric maps; `sampledAt` is the ms timestamp
// when `latest` was received. Use `deriveRates()` to compute /sec rates.
export function useMetrics(baseUrl = '', intervalMs = 1500) {
  const snap = useSource(metricsSource(baseUrl, intervalMs));
  return {
    latest: snap.latest ?? null,
    prev: snap.prev ?? null,
    sampledAt: snap.sampledAt ?? 0,
    prevSampledAt: snap.prevSampledAt ?? 0,
    error: snap.error,
  };
}

// Poll /health on a single node. Returns { health, error }.
export function useHealth(baseUrl = '', intervalMs = 5000) {
  const snap = useSource(healthSource(baseUrl, intervalMs));
  return { health: snap.health ?? null, error: snap.error };
}

// Poll an arbitrary SQL query and return its rows. Errors are surfaced as
// `error`; previous successful rows stay visible.
export function useSqlRows(sql, baseUrl = '', intervalMs = 5000) {
  const snap = useSource(sqlSource(sql, baseUrl, intervalMs));
  return {
    columns: snap.columns ?? null,
    rows: snap.rows ?? null,
    error: snap.error,
  };
}

// ── Cluster-wide hooks ──────────────────────────────────────────────
//
// These let the UI talk to a single connected node and still get per-peer
// data. They're the production path: a reverse proxy in front of one
// kinedb endpoint can serve everything we need without forwarding browser
// traffic to specific cluster members.

/**
 * Poll `SHOW NODES`. Returns
 *   { nodes: [{node, node_id, status, rss_bytes, memory_total_bytes, last_seen_us}, ...],
 *     error }
 * `nodes` retains the previous successful sample on transient errors so a
 * blip doesn't reset the rendered list.
 */
export function useNodes(intervalMs = 5000) {
  const snap = useSource(nodesSource(intervalMs));
  return { nodes: snap.nodes ?? null, error: snap.error };
}

/**
 * Poll a `SHOW <inner> ON ALL NODES` SQL command and group the result by
 * the `node` column. Returns:
 *   { byNode: {[node]: rows[]}, columns: [...inner cols (without `node`)],
 *     rows: rows[], error }
 * `rows` is the full flat result (including `node` column at index 0) for
 * callers that prefer it; `byNode` is the same data keyed by peer address.
 */
export function useClusterSqlRows(sql, intervalMs = 5000) {
  const snap = useSource(clusterSqlSource(sql, intervalMs));
  return {
    byNode: snap.byNode ?? null,
    columns: snap.columns ?? null,
    rows: snap.rows ?? null,
    error: snap.error,
  };
}

/**
 * Poll `SHOW METRICS ON ALL NODES` and parse into per-node maps. Returns
 *   { byNode: {[node]: {[key]: number}}, prevByNode: same shape from
 *     the prior tick (so callers can `deriveRates`), sampledAt, prevSampledAt,
 *     error }
 */
export function useClusterMetrics(intervalMs = 1500) {
  const snap = useSource(clusterMetricsSource(intervalMs));
  return {
    byNode: snap.byNode ?? null,
    prevByNode: snap.prevByNode ?? null,
    sampledAt: snap.sampledAt ?? 0,
    prevSampledAt: snap.prevSampledAt ?? 0,
    error: snap.error,
  };
}

// Throughput history (reads/writes per sec) sampled from SHOW METRICS at
// `intervalMs` cadence. Returns the {reads, writes, t} ring buffer
// owned by the same singleton that backs `useMetrics`, so subscribing to
// both does not double-poll the server. The buffer persists across
// route changes — pages that mount the Monitor view see a fully-
// populated chart from the first frame instead of "Awaiting samples…".
export function useThroughputHistory(intervalMs = 1500) {
  const snap = useSource(metricsSource('', intervalMs));
  return snap.history ?? [];
}

// SHOW S3 STATUS, parsed. Single source for both the lag KPIs and the
// trend sparkline (`history`). One polling loop, shared across pages.
export function useS3Status(intervalMs = 2000) {
  const snap = useSource(s3StatusSource(intervalMs));
  return {
    columns:    snap.columns ?? null,
    rows:       snap.rows ?? null,
    isDisabled: snap.isDisabled ?? true,
    local:      snap.local ?? null,
    synced:     snap.synced ?? null,
    lag:        snap.lag ?? null,
    pending:    snap.pending ?? null,
    history:    snap.history ?? [],
    error:      snap.error,
  };
}
